import { useMutation } from "@tanstack/react-query";
import {
  service,
  EDRResponse,
  useAuthConfig,
  Test,
  ThreatHunt,
  Detection,
  UpdateTestProps,
  queryClient,
  queries,
  StatusResponse,
} from "@theprelude/core";
import { notifyError } from "@theprelude/ds";
import isEqual from "lodash/isEqual";
import { Merge } from "type-fest";
import { hasFeatures } from "../flag/flag";
import { TestEditorState } from "../test-editor/controlled-test-editor";

type UpdateTestFields = Merge<
  Pick<UpdateTestProps, "name" | "technique">,
  { expectedEDRResponse: EDRResponse }
>;

export type UpdateTestForm = {
  testFields: UpdateTestFields;
  testEditor: TestEditorState;
};

export function useUpdateOwnerTestContents() {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: async ({
      test,
      readme,
      sourceCode,
      form,
    }: {
      test: Test;
      readme: string;
      sourceCode: string;
      form: UpdateTestForm;
    }) => {
      const deletePromises: Promise<StatusResponse>[] = [];
      const account = await queryClient.ensureQueryData(
        queries.iam.getAccount(authConfig),
      );
      const { testEditor, testFields } = form;

      if (hasFeatures(account, ["threat_intel"])) {
        testEditor.threatHunts
          .filter((threatHunt) => threatHunt.status === "deleted")
          .map((threatHunt) =>
            deletePromises.push(
              service(authConfig).build.deleteThreatHunt(threatHunt.id),
            ),
          );
      }

      if (hasFeatures(account, ["detections"])) {
        testEditor.detections
          .filter((detection) => detection.status === "deleted")
          .map((detection) =>
            deletePromises.push(
              service(authConfig).build.deleteDetection(detection.id),
            ),
          );
      }

      await Promise.all(deletePromises);

      const promises: Promise<ThreatHunt | StatusResponse>[] = [];

      if (hasFeatures(account, ["threat_intel"])) {
        testEditor.threatHunts
          .filter((threatHunt) => threatHunt.status === "modified")
          .map((threatHunt) =>
            promises.push(
              service(authConfig).build.updateThreatHunt({
                threat_hunt_id: threatHunt.id,
                name: threatHunt.name,
                query: threatHunt.query,
                control: threatHunt.control,
              }),
            ),
          );
        testEditor.threatHunts
          .filter((threatHunt) => threatHunt.status === "created")
          .map((threatHunt) =>
            promises.push(
              service(authConfig).build.createThreatHunt({
                test_id: test.id,
                name: threatHunt.name,
                query: threatHunt.query,
                control: threatHunt.control,
              }),
            ),
          );
      }

      if (hasFeatures(account, ["detections"])) {
        testEditor.detections
          .filter((detection) => detection.status === "modified")
          .map((detection) =>
            promises.push(
              service(authConfig).build.updateDetection({
                detectionId: detection.id,
                rule: detection.rule,
              }),
            ),
          );
        testEditor.detections
          .filter((detection) => detection.status === "created")
          .map((detection) =>
            promises.push(
              service(authConfig).build.createDetection({
                testId: test.id,
                rule: detection.rule,
              }),
            ),
          );
      }

      const uploadSourceCode = async () => {
        if (!isEqual(sourceCode, testEditor.sourceCode)) {
          await service(authConfig).build.upload({
            testId: test.id,
            filename: `${test.id}.go`,
            data: testEditor.sourceCode,
            ai_generated: true,
          });
        }
      };
      const uploadReadMe = async () => {
        if (!isEqual(readme, testEditor.readme)) {
          await service(authConfig).build.upload({
            testId: test.id,
            filename: `README.md`,
            data: testEditor.readme,
          });
        }
      };
      const updateTest = async () => {
        if (
          !isEqual(updateTest, {
            name: test.name,
            technique: test.technique,
            expectedEDRResponse: test.expected.crowdstrike,
          })
        ) {
          await service(authConfig).build.updateTest({
            testId: test.id,
            technique: testFields.technique,
            expected: { crowdstrike: testFields.expectedEDRResponse },
            name: testFields.name,
          });
        }
      };

      await Promise.allSettled([
        ...promises,
        uploadSourceCode(),
        uploadReadMe(),
        updateTest(),
      ]);
    },
    onError: (error) =>
      notifyError({
        message: "Unsuccessfully updated all test changes",
        error,
        dismissible: true,
      }),
  });
}

export function useUpdatePreludeTestContents() {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: async ({
      test,
      expectedEDRResponse,
    }: {
      test: Test;
      expectedEDRResponse: EDRResponse;
    }) => {
      return service(authConfig).build.updateTest({
        testId: test.id,
        expected: { crowdstrike: expectedEDRResponse },
      });
    },
    onError: (error) =>
      notifyError({
        message: "Unsuccessfully updated expected response",
        error,
        dismissible: true,
      }),
  });
}

export function useUpdateRegeneratedTest() {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: async ({
      test,
      intel_context,
      detections,
      threatHunts,
      testEditor,
    }: {
      test: Test;
      intel_context: string;
      detections: Detection[];
      threatHunts: ThreatHunt[];
      testEditor: TestEditorState;
    }) => {
      const deletePromises: Promise<StatusResponse>[] = [];
      const account = await queryClient.ensureQueryData(
        queries.iam.getAccount(authConfig),
      );

      if (hasFeatures(account, ["threat_intel"])) {
        threatHunts.map((threatHunt) =>
          deletePromises.push(
            service(authConfig).build.deleteThreatHunt(threatHunt.id),
          ),
        );
      }

      if (hasFeatures(account, ["detections"])) {
        detections.map((detection) =>
          deletePromises.push(
            service(authConfig).build.deleteDetection(detection.id),
          ),
        );
      }

      await Promise.all(deletePromises);

      const promises: Promise<ThreatHunt | StatusResponse>[] = [];

      if (hasFeatures(account, ["threat_intel"])) {
        testEditor.threatHunts
          .filter((threatHunt) => threatHunt.status === "created")
          .map((threatHunt) =>
            promises.push(
              service(authConfig).build.createThreatHunt({
                test_id: test.id,
                name: threatHunt.name,
                query: threatHunt.query,
                control: threatHunt.control,
              }),
            ),
          );
      }

      if (hasFeatures(account, ["detections"])) {
        testEditor.detections
          .filter((detection) => detection.status === "created")
          .map((detection) =>
            promises.push(
              service(authConfig).build.createDetection({
                testId: test.id,
                rule: detection.rule,
              }),
            ),
          );
      }

      const uploadSourceCode = async () => {
        await service(authConfig).build.upload({
          testId: test.id,
          filename: `${test.id}.go`,
          data: testEditor.sourceCode,
          ai_generated: true,
        });
      };
      const uploadReadMe = async () => {
        if (testEditor.readme) {
          await service(authConfig).build.upload({
            testId: test.id,
            filename: `README.md`,
            data: testEditor.readme,
          });
        }
      };
      const updateTest = async () => {
        await service(authConfig).build.updateTest({
          testId: test.id,
          intel_context: intel_context,
        });
      };

      await Promise.all([
        ...promises,
        uploadSourceCode(),
        uploadReadMe(),
        updateTest(),
      ]);
    },
    onError: (error) =>
      notifyError({
        message: "Unsuccessfully updated all test changes",
        error,
        dismissible: true,
      }),
  });
}
